import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell, { PageContainer } from "@/components/AppShell";
import { supabase } from "@/lib/supabaseClient";
import { useSupabaseSession } from "@/lib/SupabaseSessionContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Bell, Check, Trash2, MessageCircle, UserPlus, UserCheck, Users, Megaphone } from "lucide-react";

const ICON = {
  message: MessageCircle, friend_request: UserPlus, squad_invite: Users,
  request_accepted: UserCheck, system: Megaphone,
};

export default function NotificationsCenterPage() {
  const { ready } = useSupabaseSession();
  const { lang } = useT();
  const isDE = (lang || "de").startsWith("de");
  const [items, setItems] = useState([]);

  const refresh = useCallback(async () => {
    if (!ready || !supabase) return;
    const { data } = await supabase.from("notifications")
      .select("id, kind, title, body, link, is_read, created_at")
      .order("created_at", { ascending: false }).limit(100);
    setItems(data || []);
  }, [ready]);

  useEffect(() => { refresh(); }, [refresh]);

  const markAll = async () => {
    await supabase.from("notifications").update({ is_read: true }).eq("is_read", false);
    refresh();
  };
  const clearAll = async () => {
    await supabase.from("notifications").delete().neq("id", "00000000-0000-0000-0000-000000000000");
    refresh();
  };

  return (
    <AppShell>
      <PageContainer>
        <div className="flex items-center justify-between gap-2 mb-5">
          <h1 className="font-display text-2xl sm:text-3xl font-bold inline-flex items-center gap-2" data-testid="notifs-title">
            <Bell size={20} className="text-neon" /> {isDE ? "Benachrichtigungen" : "Notifications"}
          </h1>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={markAll} className="text-zinc-300 hover:text-neon" data-testid="notifs-mark-all">
              <Check size={14} className="mr-1" /> {isDE ? "Als gelesen" : "Mark read"}
            </Button>
            <Button variant="ghost" size="sm" onClick={clearAll} className="text-rose-300 hover:text-rose-400" data-testid="notifs-clear-all">
              <Trash2 size={14} className="mr-1" /> {isDE ? "Leeren" : "Clear"}
            </Button>
          </div>
        </div>
        {!ready ? (
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-10 text-center text-zinc-400">
            {isDE ? "Verbindung wird hergestellt…" : "Connecting…"}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-10 text-center text-zinc-500">
            {isDE ? "Keine Benachrichtigungen" : "No notifications"}
          </div>
        ) : (
          <ul className="rounded-2xl border border-white/5 bg-zinc-900/50 divide-y divide-white/5 overflow-hidden">
            {items.map((n) => {
              const Icon = ICON[n.kind] || Bell;
              const body = (
                <div className={`flex gap-3 px-4 py-4 ${n.is_read ? "opacity-60" : ""}`}>
                  <span className="w-9 h-9 rounded-lg bg-white/5 grid place-items-center text-neon shrink-0">
                    <Icon size={15} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{n.title}</p>
                    {n.body && <p className="text-xs text-zinc-400 mt-0.5">{n.body}</p>}
                    <p className="text-[10px] text-zinc-500 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  {!n.is_read && <span className="w-2 h-2 rounded-full bg-neon mt-1 shrink-0" />}
                </div>
              );
              return (
                <li key={n.id} data-testid={`notifs-item-${n.id}`}>
                  {n.link ? <Link to={n.link} className="block hover:bg-white/5">{body}</Link> : body}
                </li>
              );
            })}
          </ul>
        )}
      </PageContainer>
    </AppShell>
  );
}
